class ThemeSearchBar extends HTMLElement {
  #searchBtn;
  #searchInput;
  #closeBtn;
  #form;
  #suggestions;
  #suggestionsList;
  #status;
  #allResults;
  #abortController;
  #requestController;
  #debounceTimer;
  #searchType;
  #suggestionsCache = new Map();
  #lastPublishedSearch;

  constructor() {
    super();
    this.#searchBtn = this.querySelector('[data-theme-search-bar-btn]');
    this.#searchInput = this.querySelector('[data-theme-search-bar-input]');
    this.#closeBtn = this.querySelector('[data-theme-search-bar-close]');
    this.#form = this.querySelector('[data-theme-search-bar-form]');
    this.#suggestions = this.querySelector('[data-theme-search-bar-suggestions]');
    this.#suggestionsList = this.querySelector('[data-theme-search-bar-suggestions-list]');
    this.#status = this.querySelector('[data-theme-search-bar-status]');
    this.#allResults = this.querySelector('[data-theme-search-bar-all-results]');
  }

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#searchBtn.addEventListener('click', () => {
      this.dataset.showInput = 'true';
      requestAnimationFrame(() => this.#searchInput.focus());
    }, { signal });
    this.#closeBtn.addEventListener('click', () => {
      this.dataset.showInput = 'false';
      this.#hideSuggestions();
      this.#searchBtn.focus();
    }, { signal });
    this.#form.addEventListener('submit', (e) => this.#onSubmit(e), { signal });

    if (this.#searchType === 'video') {
      this.setValue(new URLSearchParams(window.location.search).get('other_filter') || '');
    }

    if (this.#searchType === 'default' && this.#suggestions) {
      this.#searchInput.addEventListener('input', () => this.#queueSuggestions(), { signal });
      this.#searchInput.addEventListener('focus', () => this.#queueSuggestions(), { signal });
      this.addEventListener('keydown', (e) => this.#onKeydown(e), { signal });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.#hideSuggestions();
      }, { signal });
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#debounceTimer);
    this.#requestController?.abort();
    this.#abortController.abort();
    this.#abortController = null;
  }

  static get observedAttributes() {
    return ['data-show-input', 'data-search-type'];
  }

  clear() {
    this.setValue('');
    this.#hideSuggestions();
  }

  setValue(value = '') {
    this.#searchInput.value = String(value).split(',')
      .map(term => term.trim())
      .filter(Boolean)
      .join(', ');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-show-input' && newValue === 'false') {
      this.#searchInput.value = '';
      this.#hideSuggestions();
    }

    if (name === 'data-search-type') {
      this.#searchType = newValue;
    }
  }

  #queueSuggestions() {
    clearTimeout(this.#debounceTimer);

    const query = this.#searchInput.value.trim();
    if (query.length < 2) {
      this.#requestController?.abort();
      this.#hideSuggestions();
      return;
    }

    const cacheKey = query.toLowerCase();
    const cachedItems = this.#suggestionsCache.get(cacheKey);
    if (cachedItems) {
      this.#requestController?.abort();
      this.#renderSuggestions(cachedItems, query);
      this.#publishPredictiveSearch(query, cachedItems.length ? 'results' : 'empty', cachedItems.length);
      return;
    }

    this.#requestController?.abort();
    this.#showLoadingSuggestions();
    this.#debounceTimer = window.setTimeout(() => this.#fetchSuggestions(query), 100);
  }

  async #fetchSuggestions(query) {
    this.#requestController?.abort();
    const requestController = new AbortController();
    let requestTimedOut = false;
    const requestTimeout = window.setTimeout(() => {
      requestTimedOut = true;
      requestController.abort();
    }, 8000);
    this.#requestController = requestController;
    this.#setStatus('Loading suggestions');

    const endpoint = this.dataset.predictiveUrl || '/search/suggest';
    const endpointJson = endpoint.endsWith('.json') ? endpoint : `${endpoint}.json`;
    const url = new URL(endpointJson, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', '6');
    // This site uses unavailable variants for informational catalog products.
    // Keep them searchable even though they cannot be purchased online.
    url.searchParams.set('resources[options][unavailable_products]', 'show');

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: requestController.signal
      });

      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const data = await response.json();
      if (query !== this.#searchInput.value.trim()) return;

      const results = data.resources?.results || {};
      const items = [
        ...(results.collections || []).map((item) => ({ ...item, resultType: 'Collection' })),
        ...(results.products || []).map((item) => ({ ...item, resultType: 'Product' })),
        ...(results.pages || []).map((item) => ({ ...item, resultType: 'Page' })),
        ...(results.articles || []).map((item) => ({ ...item, resultType: 'Article' }))
      ].slice(0, 6);

      this.#suggestionsCache.set(query.toLowerCase(), items);
      if (this.#suggestionsCache.size > 20) {
        this.#suggestionsCache.delete(this.#suggestionsCache.keys().next().value);
      }

      this.#renderSuggestions(items, query);
      this.#publishPredictiveSearch(query, items.length ? 'results' : 'empty', items.length);
    } catch (error) {
      if (error.name === 'AbortError' && !requestTimedOut) return;

      if (query !== this.#searchInput.value.trim()) return;
      this.#showResponseState('Suggestions unavailable', query);
      this.#publishPredictiveSearch(query, 'unavailable', 0);
    } finally {
      clearTimeout(requestTimeout);
    }
  }

  #renderSuggestions(items, query) {
    this.#suggestionsList.replaceChildren();

    items.forEach((item, index) => {
      const listItem = document.createElement('li');
      const link = document.createElement('a');
      const text = document.createElement('span');
      const title = document.createElement('span');
      const type = document.createElement('span');
      const imageUrl = this.#getImageUrl(item);

      listItem.className = 'theme-search-bar__suggestion';
      listItem.setAttribute('role', 'none');

      link.className = 'theme-search-bar__suggestion-link';
      link.href = item.url;
      link.id = `${this.#suggestionsList.id}-option-${index}`;
      link.setAttribute('role', 'option');

      if (imageUrl) {
        const media = document.createElement('span');
        const image = document.createElement('img');
        media.className = 'theme-search-bar__suggestion-media';
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
        link.append(media);
      } else {
        link.classList.add('theme-search-bar__suggestion-link--text-only');
      }

      text.className = 'theme-search-bar__suggestion-text';
      title.className = 'theme-search-bar__suggestion-title';
      title.textContent = item.title;
      type.className = 'theme-search-bar__suggestion-type';
      type.textContent = item.resultType;
      text.append(title, type);
      link.append(text);
      listItem.append(link);
      this.#suggestionsList.append(listItem);
    });

    if (!items.length) {
      this.#showResponseState('No results found.', query);
      return;
    }

    this.#setAllResultsUrl(query);
    this.#allResults.hidden = false;
    this.#suggestions.hidden = false;
    this.#searchInput.setAttribute('aria-expanded', 'true');
    this.#setStatus(`${items.length} search suggestions available`);
  }

  #showResponseState(message, query) {
    const listItem = document.createElement('li');
    listItem.className = 'theme-search-bar__response-state';
    listItem.textContent = message;

    this.#suggestionsList.replaceChildren(listItem);
    this.#setAllResultsUrl(query);
    this.#allResults.hidden = false;
    this.#suggestions.hidden = false;
    this.#searchInput.setAttribute('aria-expanded', 'true');
    this.#setStatus(message);
  }

  #setAllResultsUrl(query) {
    const allResultsUrl = new URL(this.#form.action, window.location.origin);
    allResultsUrl.searchParams.set('q', query);
    allResultsUrl.searchParams.set('options[prefix]', 'last');
    allResultsUrl.searchParams.set('options[unavailable_products]', 'show');
    this.#allResults.href = `${allResultsUrl.pathname}${allResultsUrl.search}`;
  }

  #publishPredictiveSearch(query, status, suggestionCount) {
    const normalizedQuery = query.trim();
    const eventKey = `${normalizedQuery.toLowerCase()}:${status}:${suggestionCount}`;
    if (!normalizedQuery || eventKey === this.#lastPublishedSearch) return;

    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;

    this.#lastPublishedSearch = eventKey;
    const source = this.closest('.main-search__search-bar-wrap') ? 'search-page' : 'header';
    Promise.resolve(publish.call(window.Shopify.analytics, 'sonance:predictive_search', {
      query: normalizedQuery,
      status,
      suggestionCount,
      source
    })).catch(() => {});
  }

  #showLoadingSuggestions() {
    const listItem = document.createElement('li');
    const spinner = document.createElement('span');
    const label = document.createElement('span');

    listItem.className = 'theme-search-bar__loading';
    spinner.className = 'theme-search-bar__loading-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    label.textContent = 'Searching…';
    listItem.append(spinner, label);

    this.#suggestionsList.replaceChildren(listItem);
    this.#allResults.hidden = true;
    this.#suggestions.hidden = false;
    this.#searchInput.setAttribute('aria-expanded', 'true');
    this.#setStatus('Loading suggestions');
  }

  #getImageUrl(item) {
    const image = item.image || item.featured_image || item.featured_media?.preview_image;
    if (typeof image === 'string') return image;
    return image?.url || image?.src || '';
  }

  #hideSuggestions() {
    if (!this.#suggestions) return;
    this.#suggestions.hidden = true;
    this.#searchInput.setAttribute('aria-expanded', 'false');
  }

  #setStatus(message) {
    if (this.#status) this.#status.textContent = message;
  }

  #onKeydown(event) {
    const links = Array.from(this.querySelectorAll('.theme-search-bar__suggestion-link'));

    if (event.key === 'Escape') {
      this.#hideSuggestions();
      this.#searchInput.focus();
      return;
    }

    if (this.#suggestions.hidden || !links.length) return;

    const currentIndex = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      links[currentIndex < links.length - 1 ? currentIndex + 1 : 0].focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex <= 0) {
        this.#searchInput.focus();
      } else {
        links[currentIndex - 1].focus();
      }
    }
  }

  #onSubmit(e) {
    e.preventDefault();
    switch (this.#searchType) {
      case 'video':
        {
          const url = new URL(window.location.href);
          const searchTerms = this.#searchInput.value.split(',').map(val => val.toLowerCase().trim().replaceAll(' ', ''));
          const updateTerms = [...new Set(searchTerms.filter(Boolean))].join(',');
          if (updateTerms) {
            url.searchParams.set('other_filter', updateTerms);
          } else {
            url.searchParams.delete('other_filter');
          }
          url.hash = '#library';
          if (document.querySelector('[data-resources-grid]')) {
            window.history.replaceState({}, '', url);
            window.dispatchEvent(new CustomEvent('video-library:filter', {
              detail: { otherFilter: updateTerms }
            }));
          } else {
            window.location.href = url.href;
          }
        }
        break;
    
      default:
        if (this.#searchInput.value && this.#searchInput.value.trim() !== '') {
          const url = new URL('/search', window.location.origin);
          url.searchParams.set('q', this.#searchInput.value.trim());
          url.searchParams.set('options[prefix]', 'last');
          url.searchParams.set('options[unavailable_products]', 'show');
          window.location.href = `${url.pathname}${url.search}`;
        }
        break;
    }
  }
}

customElements.define('theme-search-bar', ThemeSearchBar);
